
import React from 'react';
import { ChevronRight, ArrowUpRight, ArrowDownRight, CalendarClock } from 'lucide-react';
import { ModuleStats } from '../types';

export const ModuleCard: React.FC<ModuleStats> = ({ title, value, subValue, trend, status, cta, icon, meta }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'valid': return 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border-emerald-100 dark:border-emerald-800/50';
      case 'warning': return 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-100 dark:border-amber-800/50';
      case 'error': return 'bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-400 border-rose-100 dark:border-rose-800/50';
      case 'pending': return 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-blue-100 dark:border-blue-800/50';
      default: return 'bg-slate-50 dark:bg-slate-800/20 text-slate-700 dark:text-slate-400 border-slate-100 dark:border-slate-700/50';
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-all group overflow-hidden flex flex-col">
      <div className="p-5 flex-1">
        <div className="flex justify-between items-start mb-4">
          <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-lg group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300">
             <span dangerouslySetInnerHTML={{ __html: icon }} />
          </div>
          <div className="flex flex-col items-end">
            {trend !== undefined && (
              <div className={`flex items-center text-xs font-medium ${trend >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                {trend >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                {Math.abs(trend)}%
              </div>
            )}
            {meta?.expiryDays !== undefined && (
              <div className={`mt-1 flex items-center space-x-1 text-[10px] font-semibold ${meta.expiryDays <= 7 ? 'text-rose-500' : 'text-amber-500'}`}>
                <CalendarClock size={12} />
                <span>{meta.expiryDays}d left</span>
              </div>
            )}
          </div>
        </div>
        
        <h3 className="text-slate-500 dark:text-slate-400 text-sm font-medium mb-1 uppercase tracking-wider">{title}</h3>
        <div className="flex items-baseline space-x-2">
          <span className="text-2xl font-bold text-slate-900 dark:text-white">{value}</span>
          {subValue && <span className="text-xs text-slate-400 dark:text-slate-500">{subValue}</span>}
        </div>

        <div className={`mt-4 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${getStatusColor()}`}>
          {status === 'valid' && '● Active'}
          {status === 'warning' && '● Attention'}
          {status === 'error' && '● Critical'}
          {status === 'pending' && '● In Progress'}
        </div>
      </div>
      
      <button className="w-full py-3 px-5 text-sm font-semibold border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-blue-600 dark:text-blue-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
        {cta}
        <ChevronRight size={16} />
      </button>
    </div>
  );
};
